if (!customElements.get('real-surfboard-model-details')) {
  class RealSurfboardModelDetails extends HTMLElement {
    #controller;
    #panel;
    #button;
    #fade;
    #expanded = false;
    #frame;
    #menuObserver;

    connectedCallback() {
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      this.#frame = requestAnimationFrame(() => this.#initialize(signal));
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#menuObserver?.disconnect();
      cancelAnimationFrame(this.#frame);
      this.#button?.remove();
      this.#fade?.remove();
      this.#resetPanel();
    }

    #initialize(signal) {
      const mobile = window.matchMedia('(max-width: 750px)');
      this.#button = document.createElement('button');
      this.#button.type = 'button';
      this.#button.className = 'model-content-toggle-js btn btn-dark btn-sm';
      this.#button.innerHTML = '<span data-model-toggle-label></span><span class="model-content-toggle__icon" aria-hidden="true"><svg viewBox="0 0 12 8" width="12" height="8"><path d="m1 1.5 5 5 5-5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75"></path></svg></span>';
      this.#fade = document.createElement('div');
      this.#fade.className = 'model-content-fade-js';

      const dimensions = Array.from(this.querySelectorAll('.dimensions-wrapper'), (wrapper) => ({
        wrapper,
        inner: wrapper.querySelector('.dimensions-inner'),
      }));
      let dimensionsMeasured = false;

      const selectPanel = () => {
        this.#button.remove();
        this.#fade.remove();
        this.#resetPanel();
        this.#panel = this.querySelector(mobile.matches ? '.surfboard-model-content' : ':scope > .container');
        const panelHeight = this.#panel?.scrollHeight;
        const shortDimensions = dimensionsMeasured ? [] : dimensions.filter(({ inner }) => inner && inner.scrollHeight <= 300);
        dimensionsMeasured = true;
        shortDimensions.forEach(({ wrapper }) => wrapper.classList.add('no-collapse'));
        if (!this.#panel) return;
        this.#panel.id ||= `${this.dataset.panelId}-${mobile.matches ? 'mobile' : 'desktop'}`;
        this.#button.setAttribute('aria-controls', this.#panel.id);
        if (panelHeight <= 360) {
          this.#setExpanded(true);
          return;
        }
        this.#panel.append(this.#fade, this.#button);
        this.#setExpanded(this.#expanded);
      };
      selectPanel();
      mobile.addEventListener('change', selectPanel, { signal });

      const modelMenu = this.querySelector('.surfboard-side-menu > .d-none.d-md-block .surfboard-model-li');
      if (modelMenu) {
        const updateMenuScroll = () => {
          const scrollTop = modelMenu.scrollTop;
          const atEnd = modelMenu.scrollHeight - modelMenu.clientHeight - scrollTop <= 1;
          modelMenu.classList.toggle('is-scrolled', scrollTop > 0);
          modelMenu.classList.toggle('is-at-end', atEnd);
        };
        modelMenu.addEventListener('scroll', updateMenuScroll, { passive: true, signal });
        this.#menuObserver = new ResizeObserver(updateMenuScroll);
        this.#menuObserver.observe(modelMenu);
      }

      this.#button.addEventListener('click', () => {
        this.#setExpanded(!this.#expanded);
        if (!this.#expanded) {
          this.#frame = requestAnimationFrame(() => {
            if (!this.isConnected || this.#expanded) return;
            this.#panel.scrollTop = 0;
            this.#button.focus({ preventScroll: true });
            this.#scrollTo(this.#panel);
          });
        }
      }, { signal });

      dimensions.forEach(({ wrapper }) => {
        wrapper.querySelector('.dimensions-toggle')?.addEventListener('click', () => {
          wrapper.classList.add('expanded');
        }, { signal });
      });

      this.querySelector('a[href="#real-review"]')?.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (!this.querySelector('#real-review')) return;
        event.preventDefault();
        if (window.location.hash !== '#real-review') {
          window.history.pushState(null, '', '#real-review');
        }
        this.#revealReview();
      }, { signal });

      const followHash = () => {
        if (window.location.hash === '#real-review') this.#revealReview();
      };
      window.addEventListener('hashchange', followHash, { signal });
      followHash();
    }

    #resetPanel() {
      if (!this.#panel) return;
      this.#panel.classList.remove('model-collapsed', 'is-open');
      this.#panel.style.maxHeight = '';
      this.#panel.style.overflow = '';
      this.#panel.style.scrollMarginTop = '';
    }

    #setExpanded(expanded) {
      if (!this.#panel) return;
      this.#expanded = expanded;
      this.#panel.style.maxHeight = expanded ? 'none' : '';
      this.#panel.style.overflow = expanded ? 'visible' : '';
      this.#panel.classList.toggle('model-collapsed', !expanded);
      this.#panel.classList.toggle('is-open', expanded);
      this.#fade.hidden = expanded;
      this.#button.setAttribute('aria-expanded', String(expanded));
      this.#button.querySelector('[data-model-toggle-label]').textContent =
        expanded ? this.dataset.hideLabel : this.dataset.showLabel;
    }

    #revealReview() {
      const review = this.querySelector('#real-review');
      if (!review || !this.#panel) return;
      this.#setExpanded(true);
      this.#panel.scrollTop = 0;
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        if (!this.isConnected || !this.#expanded) return;
        review.focus({ preventScroll: true });
        this.#scrollTo(review);
      });
    }

    #scrollTo(target) {
      const stickyHeader = document.querySelector('#header-component[sticky]');
      const headerHeight = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
      target.style.scrollMarginTop = `${headerHeight + 16}px`;
      target.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'nearest' });
    }
  }

    customElements.define('real-surfboard-model-details', RealSurfboardModelDetails);
}
